import { Clock, ExternalLink, MousePointerClick, Square, Terminal } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import StepByStep from '../components/StepByStep'
import CopyBlock from '../components/CopyBlock'
import SheetSubmission from '../components/SheetSubmission'
import {
  PRACTICE_TAB, TOTALS_TAB, BAND_HEADER, TOTALS_HEADERS, PRICE_BANDS,
  CUSTOM_FUNCTION, EDITED_ROWS,
} from '../data/scriptPractice'
import { MODULE_23_CHECKS } from '../data/scriptChecks'

/**
 * Module 2 of the Apps Script course (id 23).
 *
 * Three ways a script stops needing you: a function you can call from a cell,
 * a menu item anyone in the sheet can click, and a trigger that runs when
 * nobody is there. The first two are checked through what they produce; the
 * trigger is taught and not checked, because nothing it does before it first
 * fires is visible through the API and a check that cannot fail is worse than
 * no check at all.
 */

const CUSTOM_FN = `/**
 * The band a total belongs in.
 * @customfunction
 */
function ${CUSTOM_FUNCTION}(total) {
  if (total === '' || total === null || isNaN(total)) return '';
${PRICE_BANDS.filter((b) => b.min > 0)
  .map((b) => `  if (total >= ${b.min}) return '${b.label}';`)
  .join('\n')}
  return '${PRICE_BANDS[PRICE_BANDS.length - 1].label}';
}`

const MENU_AND_TOTALS = `function onOpen() {
  SpreadsheetApp.getUi()
    .createMenu('Practice')
    .addItem('Refresh totals', 'refreshTotals')
    .addToUi();
}

function refreshTotals() {
  const book = SpreadsheetApp.getActiveSpreadsheet();
  const practice = book.getSheetByName('${PRACTICE_TAB}');
  const bands = practice.getRange(2, 5, practice.getLastRow() - 1, 1).getValues();

  const counts = {};
  bands.forEach(function (row) {
    const band = String(row[0]).trim();
    if (!band) return;
    counts[band] = (counts[band] || 0) + 1;
  });

  let totals = book.getSheetByName('${TOTALS_TAB}');
  if (!totals) totals = book.insertSheet('${TOTALS_TAB}');
  totals.clear();

  const rows = [[${TOTALS_HEADERS.map((h) => `'${h}'`).join(', ')}]];
  Object.keys(counts).forEach(function (band) {
    rows.push([band, counts[band]]);
  });

  totals.getRange(1, 1, rows.length, 2).setValues(rows);
  totals.getRange(1, 1, 1, 2).setFontWeight('bold');
}`

function SectionWhy() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">A script nobody has to open</h2>
      <p className="mb-4 text-slate-600">
        Last module you ran something from the editor. That's fine for you, once. It's no use to a
        colleague who will never open Extensions, and no use at all at seven in the morning when
        you're asleep.
      </p>
      <p className="mb-4 text-slate-600">
        Four ways to fix that, and you'll do all four in this module:
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { icon: <Terminal className="h-4 w-4" />, h: 'A function in a cell', p: `Type =${CUSTOM_FUNCTION}(D2) like any built-in formula.` },
          { icon: <MousePointerClick className="h-4 w-4" />, h: 'A menu item', p: 'Anyone with the sheet open can click it. No editor, no code.' },
          { icon: <Square className="h-4 w-4" />, h: 'A button on the sheet', p: 'The one people actually find. Sitting right next to the data it rebuilds.' },
          { icon: <Clock className="h-4 w-4" />, h: 'A trigger', p: 'It runs on a schedule whether anyone is there or not.' },
        ].map(({ icon, h, p }) => (
          <div key={h} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">{icon}{h}</div>
            <p className="text-sm text-slate-600">{p}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 text-sm text-slate-500">
        You'll need the spreadsheet from the previous module, with its{' '}
        <strong>{PRACTICE_TAB}</strong> tab and {EDITED_ROWS} rows. If you've lost it, go back and
        run that script again — it takes a minute.
      </p>
    </section>
  )
}

function SectionCustomFunction() {
  const steps = [
    {
      title: 'Add the function',
      content: (
        <div className="space-y-3">
          <p>
            Open <strong>Extensions ▸ Apps Script</strong> on your practice sheet and paste this
            underneath what's already there. Don't delete <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">buildPractice</code>{' '}
            — a file can hold as many functions as you like.
          </p>
          <CopyBlock text={CUSTOM_FN} label="Copy function" />
          <p className="text-sm text-slate-500">
            The <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">@customfunction</code>{' '}
            line in the comment is what makes it callable from a cell. Without it the function still
            works in the editor, but the spreadsheet won't know it exists.
          </p>
        </div>
      ),
    },
    {
      title: 'Use it in the sheet',
      content: (
        <div className="space-y-2">
          <p>
            Back in the spreadsheet, put <strong>{BAND_HEADER}</strong> in cell{' '}
            <strong>E1</strong>, then in <strong>E2</strong> type:
          </p>
          <CopyBlock text={`=${CUSTOM_FUNCTION}(D2)`} label="Copy formula" />
          <p>
            Press Enter, then drag it down to the last row — or double-click the little square at
            the corner of the cell.
          </p>
        </div>
      ),
      highlight: `Cut-offs: ${PRICE_BANDS.map((b) => `${b.label} is ${b.describes}`).join(', ')}.`,
    },
    {
      title: "Prove to yourself it's live",
      content: (
        <div className="space-y-2">
          <p>
            Change a Price on one row — type any number over the top of it. Two things move
            without you: the <strong>Total</strong>, because the script left a formula in that
            column rather than an answer, and then the <strong>Band</strong>, because your function
            is reading that Total.
          </p>
          <p className="text-sm text-slate-500">
            That chain is the whole idea. Neither of those cells knows what the right answer is —
            they know how to work it out, which is why they are still right after somebody edits
            the sheet. A column of answers typed in by a script would have gone stale the moment
            you touched that price.
          </p>
        </div>
      ),
      highlight: 'The check at the bottom of this page looks at what is IN those cells, not what they show — a column of correct words typed by hand will not pass.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">1. A function you can call from a cell</h2>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionMenu() {
  const steps = [
    {
      title: 'Add a menu and the job behind it',
      content: (
        <div className="space-y-3">
          <p>Paste both of these into the same file, below what's already there.</p>
          <CopyBlock text={MENU_AND_TOTALS} label="Copy script" />
          <p className="text-sm text-slate-500">
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">onOpen</code> is a name
            Google looks for: it runs by itself every time somebody opens the spreadsheet, which is
            how the menu appears without anyone doing anything.
          </p>
        </div>
      ),
    },
    {
      title: 'Reload the spreadsheet',
      content: (
        <p>
          Save the script, then refresh the spreadsheet tab in your browser. A{' '}
          <strong>Practice</strong> menu appears next to Help. Menus only show up on load, which is
          why nothing happens until you refresh — the single most common "it didn't work" here.
        </p>
      ),
    },
    {
      title: 'Click it',
      content: (
        <div className="space-y-2">
          <p>
            <strong>Practice ▸ Refresh totals</strong>. A <strong>{TOTALS_TAB}</strong> tab appears,
            counting how many rows fall in each band.
          </p>
          <p className="text-sm text-slate-500">
            Now change some Prices and click it again. The counts move. Anyone you share this sheet
            with can do that — they never see the code, and they don't need to.
          </p>
        </div>
      ),
      highlight: 'This is the version you can hand to somebody else. A script only you can run is a script that stops working the week you go on leave.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">2. A menu anyone can click</h2>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionButton() {
  const steps = [
    {
      title: 'Draw the button',
      content: (
        <div className="space-y-2">
          <p>
            In the spreadsheet: <strong>Insert ▸ Drawing</strong>. Draw a rounded rectangle, type
            something honest on it like <strong>Refresh totals</strong>, and click{' '}
            <strong>Save and close</strong>. It drops onto the sheet as an image you can drag
            wherever you like — next to the data it affects is the right answer.
          </p>
        </div>
      ),
    },
    {
      title: 'Point it at the function',
      content: (
        <div className="space-y-2">
          <p>
            Click the drawing once, then the three dots at its top right, then{' '}
            <strong>Assign script</strong>. Type the function name — no brackets, no quotes:
          </p>
          <CopyBlock text="refreshTotals" label="Copy" />
          <p className="text-sm text-slate-500">
            Click <strong>OK</strong>, then click the button. The Totals tab rebuilds. That is the
            same function the menu runs and the trigger will run — one piece of code, three ways in.
          </p>
        </div>
      ),
      highlight:
        'This is the version people actually use. A menu item lives two clicks away under a heading nobody reads; a button sits on the sheet in front of them.',
    },
    {
      title: 'Or a checkbox, if you prefer',
      content: (
        <div className="space-y-2">
          <p>
            The other pattern you'll see: <strong>Insert ▸ Tickbox</strong> in a cell, and an{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">onEdit</code> function that
            watches for it being ticked, does the work, and unticks it. More moving parts than a
            button, but it survives being copied to a new sheet — drawings sometimes lose their
            script assignment when a file is duplicated.
          </p>
        </div>
      ),
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">3. A button on the sheet itself</h2>
      <p className="mb-4 text-slate-600">
        This is the common shape of it at Finder: the thing that runs the script sits on the front
        of the sheet, where the people using it will find it without being told.
      </p>

      <div className="mb-5 rounded-xl border-2 border-amber-200 bg-amber-50 p-5">
        <h3 className="font-semibold text-amber-900">Why not just use a custom function?</h3>
        <p className="mt-1 text-sm text-amber-800">
          It's the obvious idea, and Sheets won't let you.{' '}
          <code className="rounded bg-white/70 px-1 py-0.5 text-xs">{CUSTOM_FUNCTION}</code> can read
          what it's given and return an answer, and that is all — a custom function cannot write to
          other cells, add a tab, send an email or call another service. Try it and you'll get a
          permission error, or worse, silence.
        </p>
        <p className="mt-2 text-sm text-amber-800">
          So the division is: <strong>custom functions calculate</strong>, and{' '}
          <strong>buttons, menus and triggers act</strong>. Anything that changes the sheet has to
          be started by a person clicking something or a clock going off.
        </p>
      </div>

      <StepByStep steps={steps} />
    </section>
  )
}

function SectionTrigger() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">4. Something that runs without you</h2>
      <p className="mb-4 text-slate-600">
        In the Apps Script editor, click the alarm clock in the left sidebar (<strong>Triggers</strong>),
        then <strong>Add Trigger</strong>. Choose <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">refreshTotals</code>,
        source <strong>Time-driven</strong>, <strong>Day timer</strong>, and a time of day. Save.
      </p>
      <p className="mb-4 text-slate-600">
        That's it — the totals now rebuild every morning whether anyone opens the sheet or not.
      </p>
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">It runs as you, forever</h3>
          <p className="mt-1 text-sm text-amber-800">
            A trigger uses your account every time it fires, including after you've forgotten it
            exists, and it keeps running when you change teams. If it emails, writes to another
            file, or costs money, that is being done in your name at 7am with nobody watching.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">Failures are silent by default</h3>
          <p className="mt-1 text-sm text-slate-600">
            When a triggered run fails, Google emails you — and that mail is very easy to filter
            away. A job that has been broken for three weeks looks identical to a job with nothing
            to do, so check <strong>Executions</strong> in the sidebar occasionally.
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Every past run is listed there, and clicking one shows what it did — the error if it
            failed, plus anything the script logged. You weren't there at 7am, so that log is the
            only account of what happened. Worth adding a{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">console.log</code> to any
            script before you put it on a schedule.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">Nothing below checks this step</h3>
          <p className="mt-1 text-sm text-slate-600">
            A trigger that hasn't fired yet leaves no trace in the spreadsheet, so there is nothing
            honest to check. Verify it yourself: the Triggers page lists it, and Executions shows
            each run after it happens.
          </p>
        </div>
      </div>
    </section>
  )
}

function SectionTroubleshooting() {
  const problems = [
    {
      p: `The cell shows #NAME? instead of a band`,
      s: `The spreadsheet doesn't know ${CUSTOM_FUNCTION}. Check the script is saved, and that the @customfunction line is still in the comment above it.`,
    },
    {
      p: 'The cell shows Loading… and stays there',
      s: 'Custom functions can be slow to wake up. Wait a few seconds, then press Delete on the cell and retype the formula.',
    },
    {
      p: 'No Practice menu after saving',
      s: 'Refresh the spreadsheet in your browser. onOpen only runs when the sheet loads.',
    },
    {
      p: 'Refresh totals says it cannot read a property of null',
      s: `It cannot find the ${PRACTICE_TAB} tab — check the name matches exactly, including the space.`,
    },
    {
      p: 'Clicking the button does nothing',
      s: 'Assign script takes the function name only — refreshTotals, not refreshTotals() and not in quotes. Click the drawing, then its three dots, then Assign script to check what is in there.',
    },
    {
      p: 'The button vanished after copying the sheet',
      s: 'Drawings can lose their script assignment when a file is duplicated. Re-assign it on the copy — or use the tickbox pattern, which survives.',
    },
    {
      p: `I tried to make ${CUSTOM_FUNCTION} write the Totals tab and nothing happened`,
      s: 'Custom functions cannot change anything — they calculate and return. Use the button, the menu or a trigger for anything that writes.',
    },
    {
      p: 'The counts are stale',
      s: 'They were right when the menu item last ran. It only recounts when clicked — which is exactly the problem a trigger solves.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">If you get stuck</h2>
      <dl className="space-y-3">
        {problems.map(({ p, s }) => (
          <div key={p} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{s}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-slate-500">
        Want to see where this goes next?{' '}
        <a href="/module/24" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
          The Local Loop <ExternalLink className="h-3 w-3" />
        </a>{' '}
        covers editing scripts on your own machine and keeping versions of them.
      </p>
    </section>
  )
}

export default function ModuleMakingItRepeatable() {
  return (
    <ModuleLayout moduleId={23}>
      <div className="space-y-12">
        <SectionWhy />
        <SectionCustomFunction />
        <SectionMenu />
        <SectionButton />
        <SectionTrigger />
        <SectionTroubleshooting />
        <SheetSubmission
          moduleId={23}
          checks={MODULE_23_CHECKS}
          sourceModuleId={22}
          heading="Check your sheet"
          intro="Same practice spreadsheet as the last module. This checks the function is really a function, and that your totals match the rows they came from."
        />
      </div>
    </ModuleLayout>
  )
}
